<template>
  <div class="sale-wrapper">
    <div class="card main-cards total-token-availability">
      <el-skeleton :loading="!allApr || !pearlApr" animated>
        <template #template>
          <el-skeleton-item class="chevron-apr-skeleton" />
        </template>
        <template #default>
          <UIChevron :type="ChevronType.Apr">
            <span class="chevron-apr-label">{{ $t('appStatisticAPR') }}:</span>
            <span class="chevron-apr-value">{{ pearlApr }}%</span>
          </UIChevron>
        </template>
      </el-skeleton>

      <h4 class="sale-title">{{ $t('saleStatusTotalTokenAvailability') }}</h4>

      <div class="token-supply-wrapper">
        <div class="token-info-wrapper">
          <div class="text-white">{{ $t('saleStatusForSale') }}</div>
          <div class="skeleton-wrapper">
            <el-skeleton :loading="isSaleAmountLoading" animated>
              <template #template>
                <el-skeleton-item class="text-numbers-skeleton" />
              </template>
              <template #default>
                <div class="text-numbers">
                  {{ formattedSaleAmount }}
                </div>
              </template>
            </el-skeleton>
          </div>
        </div>
        <div class="token-progress-wrapper">
          <div class="progress-bar-bg">
            <div class="progress-bar-full" :style="`width: ${percentLeft}%`" />
            <div class="progress-bar-text-wrapper">
              <div>{{ percentLeft }}%</div>
            </div>
          </div>
        </div>
        <div class="token-info-wrapper last">
          <div class="text-white">{{ $t('saleStatusAcquired') }}</div>
          <div class="skeleton-wrapper">
            <el-skeleton :loading="isSoldAmountLoading" animated>
              <template #template>
                <el-skeleton-item class="text-numbers-skeleton" />
              </template>
              <template #default>
                <div class="text-numbers">
                  {{ formattedSoldAmount }}
                </div>
              </template>
            </el-skeleton>
          </div>
        </div>
      </div>
      <div class="btns-wrapper">
        <button class="btn-outline" @click="toggleModal(ModalTypes.AVAILABILITY)">
          {{ $t('saleStatusAboutTokenAvailability') }}
        </button>
      </div>
    </div>

    <el-dialog
      v-model="isInfoModalOpen.availability"
      class="info"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="$device.isMobile"
      :title="$t('saleStatusTotalTokenAvailability')"
      :append-to-body="true"
    >
      <p class="text-block">
        {{ $t('saleStatusThisIndicatorShows') }}
      </p>
      <p class="text-block">
        {{ $t('saleStatusCaptainPegLegPete') }}
      </p>

      <div class="btns-wrapper">
        <button class="btn-primary" size="large" @click="isInfoModalOpen.availability = false">OK</button>
      </div>
    </el-dialog>

    <div class="card main-cards">
      <div class="max-token-supply-wrapper">
        <h4 class="sale-title">{{ $t('saleStatusCommunityShare') }}</h4>
        <div class="max-total-supply pearl-wrapper">
          <img src="/img/icons/pearl.png" loading="lazy" width="48" />
          <p class="total-supply">
            {{ BigNumber(2000000000).toFormat(0, BigNumber.ROUND_FLOOR, { groupSize: 3, groupSeparator: ' ' }) }}
          </p>
        </div>
        <div class="btns-wrapper">
          <button class="btn-outline" @click="toggleModal(ModalTypes.COMMUNITY)">
            {{ $t('saleStatusAboutCommunityShare') }}
          </button>
          <a class="btn-primary" :href="link" target="_blank">{{ $t('saleStatusCheckOnExplorer') }}</a>
        </div>
      </div>
    </div>
    <el-dialog
      v-model="isInfoModalOpen.community"
      class="info"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="$device.isMobile"
      :title="$t('saleStatusAboutCommunityShare')"
      :append-to-body="true"
    >
      <p class="text-block" v-html="$t('saleStatusCommunityShareMinted', { number: format(2000000000) })"></p>

      <div class="btns-wrapper">
        <button class="btn-primary" size="large" @click="isInfoModalOpen.community = false">OK</button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import useEnvs from '~/composables/useEnvs';
import { Tokens } from '~/utils/constants';
import { ChevronType } from '~/utils/constants/ui';
import type { IPercentAPRResponseNamed } from '~/types/apiService';

enum ModalTypes {
  AVAILABILITY = 'availability',
  COMMUNITY = 'community'
}

const { blockchain } = useEnvs();
const { saleAmount, soldAmount, tokenMaxTotalSupply } = useTokensReader();
const { data: allApr } = useNuxtData<IPercentAPRResponseNamed[]>('allApr-marketplace');

const isSaleAmountLoading = ref(true);
const isSoldAmountLoading = ref(true);
const saleAmountRef = ref('...');
const maxTotalSupplyRef = ref('...');

const soldAmountRef = asyncComputed(async () => {
  try {
    return await soldAmount(Tokens.pearl);
  } finally {
    isSoldAmountLoading.value = false;
  }
}, '0');
const pearlApr = computed(() => {
  if (!allApr.value) {
    return 0;
  }

  return allApr.value.find((item) => item.key === Tokens.pearl)?.apr;
});

const formatNumber = (val: string | number) =>
  new BigNumber(val).toFormat(0, BigNumber.ROUND_FLOOR, { groupSize: 3, groupSeparator: ' ' });

const formattedSaleAmount = computed(() => formatNumber(saleAmountRef.value));
const formattedSoldAmount = computed(() => formatNumber(soldAmountRef.value));

onMounted(async () => {
  try {
    [saleAmountRef.value, maxTotalSupplyRef.value] = await Promise.allSettled([
      saleAmount(Tokens.pearl),
      tokenMaxTotalSupply(Tokens.pearl)
    ]).then((results) =>
      results.map((res) => {
        if (res.status === 'fulfilled') {
          return typeof res.value === 'bigint' ? res.value.toString() : String(res.value);
        }
        return '...';
      })
    );
  } finally {
    isSaleAmountLoading.value = false;
  }
});

const isInfoModalOpen = ref<Record<ModalTypes, boolean>>({
  availability: false,
  community: false
});

const total = computed(() => BigNumber(saleAmountRef.value).plus(soldAmountRef.value).toNumber());
const link = blockchain.blockScanUrl + '/token/' + blockchain.contracts.pearl.addresses.contract;

const percent = computed(() => {
  if (saleAmountRef.value === '...') return 0;
  return new BigNumber(soldAmountRef.value).dividedBy(new BigNumber(total.value)).multipliedBy(100).toFixed(1);
});

const percentLeft = computed(() => {
  if (percent.value === '...') return '...';
  return new BigNumber(100).minus(new BigNumber(percent.value));
});

function format(value: string | number) {
  if (!value || value === '...') {
    return value;
  }
  return new BigNumber(+value).toFormat();
}

const toggleModal = (key: ModalTypes) => {
  isInfoModalOpen.value[key] = !isInfoModalOpen.value[key];
};
</script>

<style scoped lang="scss">
.total-token-availability {
  position: relative;
}

.chevron {
  position: absolute;
  top: -12px;
  right: -10px;
}

.chevron-apr-skeleton {
  width: 90px;
  height: 39px;
  position: absolute;
  top: -12px;
  right: -10px;

  @media screen and (max-width: 479px) {
    top: -20px;
  }
}

.text-numbers-skeleton {
  width: 60px;
  height: 18px;
}

.text-block {
  text-align: center;
  font-size: 24px;
  font-weight: 500;
  line-height: 35.52px;
  margin-bottom: 10px;
}

a.line-btn {
  border-radius: 86px;
  border: 1px solid var(--dark-4);
  font-size: 24px;
  text-transform: capitalize;
  padding: 12px 38px;
  margin: auto;
  color: var(--main-text-color);
  display: inline-flex;
  text-decoration: none;
}

.max-total-supply {
  justify-content: center;
  align-items: center;

  padding-bottom: 30px;
  display: flex;
}

.btns-wrapper {
  margin-top: 0;
}

@media screen and (max-width: 767px) {
  .pearl-wrapper {
    padding-top: 8px;
    padding-bottom: 16px;
  }
}

@media screen and (max-width: 479px) {
  .pearl-wrapper {
    padding-top: 0;
  }
}

.card {
  border: 1px solid var(--blue-2);
  background: var(--blue-3);
  border-radius: 12px;
  transform: translate(0);
}

.card.main-cards {
  margin-bottom: 0;
  padding: 20px 33px 33px;
}

@media screen and (max-width: 991px) {
  .card.main-cards {
    margin-bottom: 16px;
    padding: 20px;
    font-size: 16px;
    line-height: 1.63em;
  }
}

@media screen and (max-width: 767px) {
  .text-block {
    font-size: 16px;
  }

  .card.main-cards {
    padding: 16px;
    font-size: 14px;
  }
}

.sale-wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 22px;
  width: 100%;
}

@media screen and (max-width: 991px) {
  .pearl-wrapper {
    padding-top: 16px;
    padding-bottom: 24px;
  }

  .sale-wrapper {
    grid-template-columns: repeat(1, 1fr);
  }
}

.pb-wrapper {
  position: relative;
  height: 20px;
  border-radius: 40px;
  overflow: hidden;
  width: 100%;
  margin: 12px 0px;
}

.total-supply {
  color: #fff3e4;
  margin-bottom: 0;
  padding-left: 8px;
  font-family: Eczar, sans-serif;
  font-size: 48px;
  font-weight: 700;
  line-height: 100%;
}

@media screen and (max-width: 991px) {
  .total-supply {
    font-size: 24px;
  }
}

.token-supply-wrapper {
  justify-content: space-between;
  display: flex;
  margin-bottom: 30px;
}

.token-info-wrapper {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  padding-right: 16px;
  line-height: 120%;
  display: flex;
}

.token-progress-wrapper {
  flex-direction: column;
  flex: 1;
  justify-content: center;
  align-items: center;
  display: flex;
}

.progress-bar-bg {
  width: 100%;
  height: 40px;
  background-image: linear-gradient(#17353f, #000);
  border: 1px solid #3b7285;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.progress-bar-full {
  background-image: radial-gradient(circle at 100%, #60ff7b, #00bfff);
  border-right: 1px solid #8cfd9f;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
}

.progress-bar-text-wrapper {
  justify-content: center;
  align-items: center;
  font-weight: 800;
  display: flex;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
  font-family: Grenze, sans-serif;
  color: var(--main-text-color);
}

.token-info-wrapper.last {
  padding-left: 16px;
  padding-right: 0;
}

.text-white {
  color: var(--main-text-color);
  font-family: Grenze, sans-serif;
}

.text-numbers {
  font-family: Grenze, sans-serif;
}

@media screen and (max-width: 991px) {
  .token-info-wrapper {
    align-items: flex-start;
    padding-right: 8px;
    font-size: 14px;
  }

  .token-info-wrapper.last {
    align-items: flex-end;
    padding-left: 8px;
  }

  .token-supply-wrapper {
    flex-wrap: wrap;
    margin-top: 12px;
  }

  .token-progress-wrapper {
    width: 100%;
    flex: 1 0 auto;
    order: -1;
    margin-bottom: 8px;
  }
}
</style>

<style>
.total-token-availability .chevron-apr {
  padding: 11px 16px;
  font-size: 20px;
  height: 39px;

  @media screen and (max-width: 479px) {
    font-size: 16px;
    padding: 8px;
    top: -20px;
  }
}
</style>
